import { Button, IntlEnum, DatePicker, Typography, Alert, SnackBar } from "@common/components";
import { useExportButton, useGridView } from "@common/context/GridView/useGridView";
import { Grid } from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { format } from 'date-fns';
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { SnackbarProps } from "@mui/material";
import { useEffect, useState, useRef } from 'react';

export function setDateFilter(date: Date, isBefore: boolean = false): string {
  isBefore ? date.setHours(23, 59, 59) : date.setHours(0, 0, 0, 0);
  return format(date, "yyy-MM-dd HH:mm:ss");
}

export function validateCompleteDateFormat(date: Date): boolean {

  if (isNaN(date.getTime())) {
    return false;
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();
  const dateString = `${day}/${month}/${year}`;

  const completeDateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/;
  return completeDateRegex.test(dateString);
}

export function normalizeDate(date: Date): Date {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

const date = new Date();

export const minDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 30);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const maxDate = () => new Date(date.setHours(23, 59, 59, 999));

export type FilterTopProps = {
  begin_date: Date;
  end_date: Date;
};

const schema = yup.object({
  begin_date: yup
    .date()
    .nullable()
    .required('Campo obrigatório')
    .test(
      "dates-test",
      "Data inicial deve ser menor ou igual à final",
      (value, context) => {
        const startDate = normalizeDate(new Date(context.parent.begin_date));
        const endDate = normalizeDate(new Date(context.parent.end_date));
        return startDate <= endDate;
      }
    )
    .test(
      "dates-test",
      "Formato de data inválido",
      (value, context) => {
        const startDate = new Date(context.parent.begin_date);
        return validateCompleteDateFormat(startDate);
      }
    )
    .test(
      "dates-test",
      "Data deve ser dos últimos 30 meses",
      (value, context) => {
        const startDate = normalizeDate(new Date(context.parent.begin_date));
        return startDate > minDate();
      }
    )
    .typeError('Formato de data inválido'),
  end_date: yup
    .date()
    .nullable()
    .required('Campo obrigatório')
    .test(
      "dates-test",
      "Formato de data inválido",
      (value, context) => {
        const endDate = new Date(context.parent.end_date);
        return validateCompleteDateFormat(endDate);
      }
    )
    .test(
      "dates-test",
      "Data final deve ser maior ou igual à inicial",
      (value, context) => {
        const startDate = normalizeDate(new Date(context.parent.begin_date));
        const endDate = normalizeDate(new Date(context.parent.end_date));
        return endDate >= startDate;
      }
    )
    .test(
      "dates-test",
      "Data final deve ser menor ou igual a hoje",
      (value, context) => {
        const endDate = normalizeDate(new Date(context.parent.end_date));
        const today = normalizeDate(new Date());
        return today >= endDate;
      }
    )
    .typeError('Formato de data inválido'),
});

export function FilterTop(props: FilterTopProps) {

  const [open, setOpen] = useState(false);
  const maxDateSpan = process?.env?.REACT_APP_MAX_DATE_SPAN ? Number(process?.env?.REACT_APP_MAX_DATE_SPAN) : 30
  const { setDisableExportButton } = useExportButton();
  const { setGridFilters, gridFilters, items } = useGridView();

  useEffect(() => {
    if (items) {
      if (items?.pageInfo?.total <= 0) {
        setDisableExportButton(true)
      }
    }
  }, [items, setDisableExportButton])

  const handleClose = () => {
    setOpen(false);
  };

  const alertProps = {
    severity: "error",
    sx: { width: '100%' },
    children: (
      <Typography>
        <IntlEnum name="date_filter" value="max_date_span_30_days" />
      </Typography>
    ),
  }

  const snackProps: SnackbarProps = {
    autoHideDuration: 3000,
    anchorOrigin: {
      vertical: "top",
      horizontal: "center"
    },
    children: Alert(alertProps),
    open: open,
    onClose: handleClose
  };

  const onSubmit: SubmitHandler<FilterTopProps> = (data: FilterTopProps) => {
    let filters = gridFilters

    const oneDay = 24 * 60 * 60 * 1000
    const diff = Math.abs((new Date(data.begin_date).getTime() - new Date(data.end_date).getTime()) / oneDay);

    if (Math.floor(diff) > maxDateSpan) {
      setOpen(true);
      return
    }

    filters = filters.filter((filter) => {
      return filter.id !== 'begin_date' && filter.id !== 'end_date'
    })

    filters.push(
      { "id": "begin_date", "value": setDateFilter(data.begin_date) },
      { "id": "end_date", "value": setDateFilter(data.end_date, true) },
    )

    setGridFilters(filters);
    setDisableExportButton(false)
  }

  const { handleSubmit, control, formState: { errors, isValid }, watch, trigger } = useForm<FilterTopProps>(
    {
      defaultValues: props,
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: yupResolver(schema)
    }
  );

  const beginDate = watch('begin_date');
  const endDate = watch('end_date');
  const prevBeginDateRef = useRef(beginDate);
  const prevEndDateRef = useRef(endDate);

  useEffect(() => {
    if (prevBeginDateRef.current !== beginDate) {
      trigger('end_date');
      prevBeginDateRef.current = beginDate;
    }
  }, [beginDate, trigger]);

  useEffect(() => {
    if (prevEndDateRef.current !== endDate) {
      trigger('begin_date');
      prevEndDateRef.current = endDate;
    }
  }, [endDate, trigger]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SnackBar {...snackProps} />
      <Grid
        container
        direction="row"
        justifyContent="center"
        columnSpacing={1}
        sx={{ marginBottom: 4 }}
      >
        <Grid item xs={2.0} lg={2.0}>
          <DatePicker
            label="Data Inicial"
            data-testid="begin_date"
            control={control}
            controllerName="begin_date"
            errorMessage={errors.begin_date?.message}
            minDate={minDate()}
            maxDate={maxDate()}
          />
        </Grid>
        <Grid item xs={2.0} lg={2.0}>
          <DatePicker
            label="Data Final"
            control={control}
            data-testid="end_date"
            controllerName="end_date"
            errorMessage={errors.end_date?.message}
            minDate={minDate()}
          />
        </Grid>
        <Grid
          item
          xs={1}
          container
        >
          <Grid item>
            <Button disabled={!isValid} size="medium" type="submit">BUSCAR </Button>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
}
